import * as RadixPopover from "@radix-ui/react-popover";
import { Icon, Tooltip, token } from "@may-i/mash-design-system";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Kbd } from "~/shared/ui/Kbd";

/**
 * F20: 캔버스 그리기 단축키 안내 패널 (정적 골격).
 * 캔버스 툴바의 `?` 버튼으로 토글. 콘텐츠 = F19 에서 정의한 그리기 단축키.
 * 라이브 하이라이트/테스트 등 인터랙션 없음 — 카테고리별로 보여주기만.
 */

interface ShortcutItem {
  keys: ReactNode[];
  descKey: string;
}

interface ShortcutGroup {
  titleKey: string;
  items: ShortcutItem[];
}

const GROUPS: ShortcutGroup[] = [
  {
    titleKey: "shortcut.group.drawing",
    items: [
      { keys: ["M"], descKey: "shortcut.toggleZoneMode" },
      { keys: ["Shift", "+", "drag"], descKey: "shortcut.straightLine" },
      { keys: ["dblclick"], descKey: "shortcut.completePolygon" },
      { keys: ["Esc"], descKey: "shortcut.cancelDrawing" },
    ],
  },
  {
    titleKey: "shortcut.group.edit",
    items: [
      { keys: ["Delete", "Backspace"], descKey: "shortcut.deleteSelected" },
    ],
  },
];

export function ShortcutGuide() {
  const { t } = useTranslation("analysis-draft");
  const [open, setOpen] = useState(false);

  // 키 토큰 렌더 — 특수 토큰(+, drag, dblclick)은 별도 표기
  const renderKey = (k: ReactNode, i: number) => {
    if (k === "+") return <KeySep key={i}>+</KeySep>;
    if (k === "drag") return <Kbd key={i}>{t("shortcut.token.drag")}</Kbd>;
    if (k === "dblclick") return <Kbd key={i}>{t("shortcut.token.dblclick")}</Kbd>;
    return <Kbd key={i}>{k}</Kbd>;
  };

  return (
    <RadixPopover.Root open={open} onOpenChange={setOpen}>
      <Tooltip
        side="top"
        sideOffset={6}
        trigger={
          <RadixPopover.Trigger asChild>
            <TriggerButton type="button" aria-label={t("shortcut.tooltip")} $active={open}>
              <Icon icon="ic_question" width={16} height={16} />
            </TriggerButton>
          </RadixPopover.Trigger>
        }
      >
        {t("shortcut.tooltip")}
      </Tooltip>
      <RadixPopover.Portal>
        <Content sideOffset={6} align="end">
          <Title>{t("shortcut.title")}</Title>
          {GROUPS.map((group) => (
            <Group key={group.titleKey}>
              <GroupTitle>{t(group.titleKey)}</GroupTitle>
              {group.items.map((item) => (
                <Row key={item.descKey}>
                  <Keys>{item.keys.map(renderKey)}</Keys>
                  <Desc>{t(item.descKey)}</Desc>
                </Row>
              ))}
            </Group>
          ))}
        </Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}

// --- Styles ---

const TriggerButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: ${({ $active }) => ($active ? "rgba(72, 75, 210, 0.08)" : "transparent")};
  color: ${({ $active }) => ($active ? "#484BD2" : token("colors.text.secondary"))};
  cursor: pointer;

  &:hover {
    background: ${token("colors.action.hover")};
  }
`;

const Content = styled(RadixPopover.Content)`
  width: 320px;
  padding: 16px;
  border-radius: 16px;
  border: 1px solid #e6e8f0;
  background: #fff;
  box-shadow:
    -20px 20px 40px -4px rgba(105, 111, 140, 0.24),
    0px 0px 2px 0px rgba(105, 111, 140, 0.16);
  outline: none;
  z-index: 50;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Title = styled.div`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  color: #11111f;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GroupTitle = styled.div`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  color: #696f8c;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
`;

const Keys = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
`;

const KeySep = styled.span`
  font-size: 11px;
  color: #8f95b2;
`;

const Desc = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.4;
  color: ${token("colors.text.primary")};
  text-align: right;
  white-space: nowrap;
`;
